import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { FormControlValidators } from '../../../core/component/forms/form-control.validators';
import { finalize } from 'rxjs/operators';
import { FormControlEx } from '../../../core/component/forms/form-control';
import { SelectionItem } from '../../../core/component/select/select.component';
import { ActionWindowService } from '../../../core/service/action-window.service';
import { FormControlExFactory } from '../../../core/service/factory/form-control-ex-factory.service';
import { Company } from '../../model/company';
import {
  AllowedOnlineTerminationSignatureKind,
  CompanySystemSettings,
  CompanySystemSettingsBulkTemplate,
  CompanySystemSettingsBulkTarget,
  GetReminderDeliveryMethodIcon,
  ReminderDeliveryMethod,
  TenantPageSource,
} from '../../model/company-system-settings';
import { CompanyService } from '../../service/data/company.service';

@Component({
  selector: 'app-company-system-settings-bulk',
  templateUrl: './company-system-settings-bulk.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class CompanySystemSettingsBulkComponent implements OnInit {
  constructor(private _dialogService: ActionWindowService, private _cf: FormControlExFactory, private _service: CompanyService) {}

  @Input() dataContext: {
    companies?: Company[];
    companyIds?: string[];
    excludedCompanyIds?: string[];
    filter?: any;
    totalAffectedItems?: number;
  };
  @Input() useActionWindow = true;
  @Output() completed = new EventEmitter<number>();
  @Output() canceled = new EventEmitter<void>();

  form: FormGroup;
  working = false;
  bulkTarget = CompanySystemSettingsBulkTarget;
  private template: CompanySystemSettings;
  targets = [
    new SelectionItem('COMPANY.SYSTEM_SETTINGS_BULK.TARGET.INVOICE_REMINDER', CompanySystemSettingsBulkTarget.InvoiceReminder),
    new SelectionItem('COMPANY.SYSTEM_SETTINGS_BULK.TARGET.AUTOMATIC_ADVICING', CompanySystemSettingsBulkTarget.AutomaticAdvicing),
    new SelectionItem('COMPANY.SYSTEM_SETTINGS_BULK.TARGET.FAULT_NOTIFICATION', CompanySystemSettingsBulkTarget.FaultNotification),
    new SelectionItem('COMPANY.SYSTEM_SETTINGS_BULK.TARGET.TENANT_PAGES', CompanySystemSettingsBulkTarget.TenantPages),
  ];
  reminderDeliveryMethods = [
    new SelectionItem('COMPANY.SYSTEM_SETTINGS_BULK.REMINDER_DELIVERY_METHOD.NONE', ReminderDeliveryMethod.None, null, null, GetReminderDeliveryMethodIcon(ReminderDeliveryMethod.None)),
    new SelectionItem('COMPANY.SYSTEM_SETTINGS_BULK.REMINDER_DELIVERY_METHOD.EMAIL', ReminderDeliveryMethod.Email, null, null, GetReminderDeliveryMethodIcon(ReminderDeliveryMethod.Email)),
    new SelectionItem('COMPANY.SYSTEM_SETTINGS_BULK.REMINDER_DELIVERY_METHOD.MANUAL', ReminderDeliveryMethod.Manual, null, null, GetReminderDeliveryMethodIcon(ReminderDeliveryMethod.Manual)),
    new SelectionItem('COMPANY.SYSTEM_SETTINGS_BULK.REMINDER_DELIVERY_METHOD.MAIL', ReminderDeliveryMethod.Mail, null, null, GetReminderDeliveryMethodIcon(ReminderDeliveryMethod.Mail)),
  ];
  tenantPageSources = [
    new SelectionItem('COMPANY.SYSTEM_SETTINGS_BULK.TENANT_PAGE_SOURCE.BUILT_IN', TenantPageSource.BuiltIn),
    new SelectionItem('COMPANY.SYSTEM_SETTINGS_BULK.TENANT_PAGE_SOURCE.EXTERNAL', TenantPageSource.External),
  ];
  tenantTerminationFields = [
    {
      caption: 'COMPANY.SYSTEM_SETTINGS_BULK.TENANT_PAGE_TERMINATION.DWELLING',
      printControl: 'allowPrintTerminationFormDwelling',
      onlineControl: 'allowTerminationOnlineDwelling',
    },
    {
      caption: 'COMPANY.SYSTEM_SETTINGS_BULK.TENANT_PAGE_TERMINATION.PREMISES',
      printControl: 'allowPrintTerminationFormPremises',
      onlineControl: 'allowTerminationOnlinePremises',
    },
    {
      caption: 'COMPANY.SYSTEM_SETTINGS_BULK.TENANT_PAGE_TERMINATION.PARKING',
      printControl: 'allowPrintTerminationFormParking',
      onlineControl: 'allowTerminationOnlineParking',
    },
    {
      caption: 'COMPANY.SYSTEM_SETTINGS_BULK.TENANT_PAGE_TERMINATION.OTHER',
      printControl: 'allowPrintTerminationFormOther',
      onlineControl: 'allowTerminationOnlineOther',
    },
  ];
  allowedOnlineTerminationSignatureKinds = [
    new SelectionItem(
      'COMPANY.SYSTEM_SETTINGS_BULK.ALLOWED_ONLINE_TERMINATION_SIGNATURE.ONLY_BANKID',
      AllowedOnlineTerminationSignatureKind.OnlyBankID
    ),
    new SelectionItem(
      'COMPANY.SYSTEM_SETTINGS_BULK.ALLOWED_ONLINE_TERMINATION_SIGNATURE.BANKID_AND_CLICK_SIGN',
      AllowedOnlineTerminationSignatureKind.BankIDAndClickSign
    ),
  ];

  get target(): CompanySystemSettingsBulkTarget {
    return this.form?.controls['target'].value;
  }

  get enabled(): boolean {
    return this.form?.controls['enabled'].value;
  }

  get tenantPageSource(): TenantPageSource {
    return this.form?.controls['tenantPageSource'].value;
  }

  get sendLandlordNotification(): boolean {
    return this.form?.controls['sendLandlordNotification'].value;
  }

  get showDetails(): boolean {
    return !!this.enabled;
  }

  get showFaultNotificationReceivers(): boolean {
    return this.showDetails && this.target === CompanySystemSettingsBulkTarget.FaultNotification && this.sendLandlordNotification;
  }

  get showBuiltInTenantPageSettings(): boolean {
    return this.showDetails && this.target === CompanySystemSettingsBulkTarget.TenantPages && this.tenantPageSource === TenantPageSource.BuiltIn;
  }

  get showExternalTenantPageSettings(): boolean {
    return this.showDetails && this.target === CompanySystemSettingsBulkTarget.TenantPages && this.tenantPageSource === TenantPageSource.External;
  }

  get affectedCompaniesCount(): number {
    return this.dataContext?.totalAffectedItems ?? this.dataContext?.companyIds?.length ?? this.dataContext?.companies?.length ?? 0;
  }

  ngOnInit(): void {
    this.form = new FormGroup({
      target: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TARGET', CompanySystemSettingsBulkTarget.InvoiceReminder),
      enabled: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.ENABLED', true),
      remindAfterDays: this._cf.createWithOptions('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.REMIND_AFTER_DAYS', 7, { minValue: 0 }),
      reminderDeliveryMethod: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.REMINDER_DELIVERY_METHOD', ReminderDeliveryMethod.Email),
      automaticAdvicingDayInMonth: this._cf.createWithOptions('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.AUTOMATIC_ADVICING_DAY', 10, {
        minValue: 1,
        maxValue: 31,
        validator: Validators.required,
      }),
      sendLandlordNotification: this._cf.create(
        'COMPANY.SYSTEM_SETTINGS_BULK.FIELD.SEND_LANDLORD_NOTIFICATION',
        false,
        null,
        null,
        'COMPANY.SYSTEM_SETTINGS_BULK.HELP_TEXT.SEND_LANDLORD_NOTIFICATION'
      ),
      faultNotificationReceivers: this._cf.create(
        'COMPANY.SYSTEM_SETTINGS_BULK.FIELD.FAULT_NOTIFICATION_RECEIVERS',
        '',
        this.emailListValidator
      ),
      tenantPageSource: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TENANT_PAGE_SOURCE', TenantPageSource.BuiltIn),
      tenantPageExternalUrl: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TENANT_PAGE_EXTERNAL_URL', ''),
      invoiceRegistrationMessage: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.INVOICE_REGISTRATION_MESSAGE', ''),
      invoiceRegistrationMessageRow2: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.INVOICE_REGISTRATION_MESSAGE_ROW2', ''),
      invoiceHasAccountMessage: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.INVOICE_HAS_ACCOUNT_MESSAGE', ''),
      invoiceHasAccountMessageRow2: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.INVOICE_HAS_ACCOUNT_MESSAGE_ROW2', ''),
      allowPrintTerminationFormDwelling: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_FORM', true),
      allowPrintTerminationFormOther: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_FORM', true),
      allowPrintTerminationFormParking: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_FORM', true),
      allowPrintTerminationFormPremises: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_FORM', true),
      allowTerminationOnlineDwelling: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_ONLINE', false),
      allowTerminationOnlineOther: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_ONLINE', false),
      allowTerminationOnlineParking: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_ONLINE', false),
      allowTerminationOnlinePremises: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.TERMINATION_ONLINE', false),
      allowedOnlineTerminationSignature: this._cf.create(
        'COMPANY.SYSTEM_SETTINGS_BULK.FIELD.ALLOWED_ONLINE_TERMINATION_SIGNATURE',
        AllowedOnlineTerminationSignatureKind.OnlyBankID
      ),
      displayDwellingInspection: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.DISPLAY_DWELLING_INSPECTION', false),
      onlineTerminlationCallbackEmailAddresses: this._cf.create('COMPANY.SYSTEM_SETTINGS_BULK.FIELD.ONLINE_TERMINATION_CALLBACK_EMAILS', ''),
    });

    this._service.getSystemSettingsBulkTemplate(this.createTemplateRequest()).subscribe(settings => this.patchTemplate(settings));

    this.form.controls['target'].valueChanges.subscribe(target => this.patchEnabledForTarget(target));
    this.form.controls['enabled'].valueChanges.subscribe(() => this.updateDetailControlState());
    this.form.controls['sendLandlordNotification'].valueChanges.subscribe(() => this.updateFaultNotificationControlState());
    this.form.controls['tenantPageSource'].valueChanges.subscribe(() => this.updateTenantPageControlState());
    this.updateDetailControlState();
  }

  cancel(): void {
    if (this.useActionWindow) this._dialogService.close();
    this.canceled.emit();
  }

  save(): void {
    if (this.working) return;
    this.updateTenantPageExternalUrlValidation();
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    this.working = true;
    this._service
      .bulkUpdateSystemSettings({
        companyIds: this.dataContext.companyIds ?? this.dataContext.companies?.map(company => company.id) ?? [],
        excludedCompanyIds: this.dataContext.excludedCompanyIds ?? [],
        filter: this.dataContext.filter,
        target: this.target,
        settings: this.createSettingsPayload(),
      })
      .pipe(finalize(() => (this.working = false)))
      .subscribe(result => {
        if (this.useActionWindow) this._dialogService.complete(result);
        this.completed.emit(result);
      });
  }

  private createSettingsPayload(): any {
    const value = this.form.getRawValue();
    switch (this.target) {
      case CompanySystemSettingsBulkTarget.InvoiceReminder:
        return {
          invoiceReminder: {
            enabled: value.enabled,
            remindAfterDays: value.remindAfterDays,
            reminderDeliveryMethodEmailWithPDF: value.reminderDeliveryMethod,
            reminderDeliveryMethodEmailWithoutPDF: value.reminderDeliveryMethod,
            reminderDeliveryMethodMail: value.reminderDeliveryMethod,
            reminderDeliveryMethodManual: value.reminderDeliveryMethod,
            reminderDeliveryMethodEInvoiceB2B: value.reminderDeliveryMethod,
            reminderDeliveryMethodEInvoiceB2C: value.reminderDeliveryMethod,
            reminderDeliveryMethodAvy: value.reminderDeliveryMethod,
            reminderDeliveryMethodKivra: value.reminderDeliveryMethod,
            reminderDeliveryMethodNoDelivery: value.reminderDeliveryMethod,
            reminderDeliveryMethodSMS: value.reminderDeliveryMethod,
          },
        };

      case CompanySystemSettingsBulkTarget.AutomaticAdvicing:
        return {
          automaticAdvicing: {
            enabled: value.enabled,
            automaticAdvicingDayInMonth: value.automaticAdvicingDayInMonth,
          },
        };

      case CompanySystemSettingsBulkTarget.FaultNotification:
        return {
          faultNotification: {
            enabled: value.enabled,
            sendLandlordNotification: value.sendLandlordNotification,
            faultNotificationReceivers: value.sendLandlordNotification ? this.splitList(value.faultNotificationReceivers) : [],
          },
        };

      case CompanySystemSettingsBulkTarget.TenantPages:
        return {
          tenantPages: {
            enabled: value.enabled,
            tenantPageSource: value.tenantPageSource,
            tenantPageExternalUrl: value.tenantPageExternalUrl,
            invoiceRegistrationMessage: value.invoiceRegistrationMessage,
            invoiceRegistrationMessageRow2: value.invoiceRegistrationMessageRow2,
            invoiceHasAccountMessage: value.invoiceHasAccountMessage,
            invoiceHasAccountMessageRow2: value.invoiceHasAccountMessageRow2,
            allowPrintTerminationFormDwelling: value.allowPrintTerminationFormDwelling,
            allowPrintTerminationFormOther: value.allowPrintTerminationFormOther,
            allowPrintTerminationFormParking: value.allowPrintTerminationFormParking,
            allowPrintTerminationFormPremises: value.allowPrintTerminationFormPremises,
            allowTerminationOnlineDwelling: value.allowTerminationOnlineDwelling,
            allowTerminationOnlineOther: value.allowTerminationOnlineOther,
            allowTerminationOnlineParking: value.allowTerminationOnlineParking,
            allowTerminationOnlinePremises: value.allowTerminationOnlinePremises,
            allowedOnlineTerminationSignature: value.allowedOnlineTerminationSignature,
            displayDwellingInspection: value.displayDwellingInspection,
            onlineTerminlationCallbackEmailAddresses: value.onlineTerminlationCallbackEmailAddresses,
          },
        };
    }
  }

  private createTemplateRequest(): CompanySystemSettingsBulkTemplate {
    return {
      companyIds: this.dataContext?.companyIds ?? this.dataContext?.companies?.map(company => company.id) ?? [],
      excludedCompanyIds: this.dataContext?.excludedCompanyIds ?? [],
      filter: this.dataContext?.filter,
    };
  }

  private patchTemplate(settings: CompanySystemSettings): void {
    if (!settings) return;

    this.template = settings;
    this.form.patchValue({
      remindAfterDays: settings.invoiceReminder?.remindAfterDays ?? 7,
      reminderDeliveryMethod: settings.invoiceReminder?.reminderDeliveryMethodEmailWithPDF ?? ReminderDeliveryMethod.Email,
      automaticAdvicingDayInMonth: settings.automaticAdvicing?.automaticAdvicingDayInMonth ?? 10,
      sendLandlordNotification: settings.faultNotification?.sendLandlordNotification ?? false,
      faultNotificationReceivers: (settings.faultNotification?.faultNotificationReceivers ?? []).join(';'),
      tenantPageSource:
        settings.tenantPages?.tenantPageSource != null && settings.tenantPages.tenantPageSource !== TenantPageSource.None
          ? settings.tenantPages.tenantPageSource
          : TenantPageSource.BuiltIn,
      tenantPageExternalUrl: settings.tenantPages?.tenantPageExternalUrl ?? '',
      invoiceRegistrationMessage: settings.tenantPages?.invoiceRegistrationMessage ?? '',
      invoiceRegistrationMessageRow2: settings.tenantPages?.invoiceRegistrationMessageRow2 ?? '',
      invoiceHasAccountMessage: settings.tenantPages?.invoiceHasAccountMessage ?? '',
      invoiceHasAccountMessageRow2: settings.tenantPages?.invoiceHasAccountMessageRow2 ?? '',
      allowPrintTerminationFormDwelling: settings.tenantPages?.allowPrintTerminationFormDwelling ?? true,
      allowPrintTerminationFormOther: settings.tenantPages?.allowPrintTerminationFormOther ?? true,
      allowPrintTerminationFormParking: settings.tenantPages?.allowPrintTerminationFormParking ?? true,
      allowPrintTerminationFormPremises: settings.tenantPages?.allowPrintTerminationFormPremises ?? true,
      allowTerminationOnlineDwelling: settings.tenantPages?.allowTerminationOnlineDwelling ?? false,
      allowTerminationOnlineOther: settings.tenantPages?.allowTerminationOnlineOther ?? false,
      allowTerminationOnlineParking: settings.tenantPages?.allowTerminationOnlineParking ?? false,
      allowTerminationOnlinePremises: settings.tenantPages?.allowTerminationOnlinePremises ?? false,
      allowedOnlineTerminationSignature:
        settings.tenantPages?.allowedOnlineTerminationSignature ?? AllowedOnlineTerminationSignatureKind.OnlyBankID,
      displayDwellingInspection: settings.tenantPages?.displayDwellingInspection ?? false,
      onlineTerminlationCallbackEmailAddresses: settings.tenantPages?.onlineTerminlationCallbackEmailAddresses ?? '',
    });
    this.patchEnabledForTarget(this.target);
    this.updateDetailControlState();
  }

  private patchEnabledForTarget(target: CompanySystemSettingsBulkTarget): void {
    if (!this.template) return;

    switch (target) {
      case CompanySystemSettingsBulkTarget.InvoiceReminder:
        this.form.controls['enabled'].setValue(this.template.invoiceReminder?.enabled ?? true, { emitEvent: false });
        break;
      case CompanySystemSettingsBulkTarget.AutomaticAdvicing:
        this.form.controls['enabled'].setValue(this.template.automaticAdvicing?.enabled ?? true, { emitEvent: false });
        break;
      case CompanySystemSettingsBulkTarget.FaultNotification:
        this.form.controls['enabled'].setValue(this.template.faultNotification?.enabled ?? true, { emitEvent: false });
        break;
      case CompanySystemSettingsBulkTarget.TenantPages:
        this.form.controls['enabled'].setValue(this.template.tenantPages?.enabled ?? true, { emitEvent: false });
        break;
    }

    this.updateDetailControlState();
  }

  private updateDetailControlState(): void {
    const detailControls = [
      'remindAfterDays',
      'reminderDeliveryMethod',
      'automaticAdvicingDayInMonth',
      'sendLandlordNotification',
      'faultNotificationReceivers',
      'tenantPageSource',
      'tenantPageExternalUrl',
      'invoiceRegistrationMessage',
      'invoiceRegistrationMessageRow2',
      'invoiceHasAccountMessage',
      'invoiceHasAccountMessageRow2',
      'allowPrintTerminationFormDwelling',
      'allowPrintTerminationFormOther',
      'allowPrintTerminationFormParking',
      'allowPrintTerminationFormPremises',
      'allowTerminationOnlineDwelling',
      'allowTerminationOnlineOther',
      'allowTerminationOnlineParking',
      'allowTerminationOnlinePremises',
      'allowedOnlineTerminationSignature',
      'displayDwellingInspection',
      'onlineTerminlationCallbackEmailAddresses',
    ];

    for (const controlName of detailControls) {
      const control = this.form.controls[controlName];
      if (!control) continue;

      if (this.enabled) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    }

    if (this.enabled && this.target === CompanySystemSettingsBulkTarget.TenantPages) {
      this.updateTenantPageControlState();
    }

    this.updateTenantPageExternalUrlValidation();

    if (this.enabled && this.target === CompanySystemSettingsBulkTarget.FaultNotification) {
      this.updateFaultNotificationControlState();
    }
  }

  private updateFaultNotificationControlState(): void {
    if (!this.enabled || this.target !== CompanySystemSettingsBulkTarget.FaultNotification) return;

    const control = this.form.controls['faultNotificationReceivers'];
    if (this.sendLandlordNotification) {
      control.enable({ emitEvent: false });
    } else {
      control.disable({ emitEvent: false });
    }
  }

  private updateTenantPageControlState(): void {
    if (!this.enabled || this.target !== CompanySystemSettingsBulkTarget.TenantPages) return;

    const builtInControlNames = [
      'invoiceRegistrationMessage',
      'invoiceRegistrationMessageRow2',
      'invoiceHasAccountMessage',
      'invoiceHasAccountMessageRow2',
      'allowPrintTerminationFormDwelling',
      'allowPrintTerminationFormOther',
      'allowPrintTerminationFormParking',
      'allowPrintTerminationFormPremises',
      'allowTerminationOnlineDwelling',
      'allowTerminationOnlineOther',
      'allowTerminationOnlineParking',
      'allowTerminationOnlinePremises',
      'allowedOnlineTerminationSignature',
      'displayDwellingInspection',
      'onlineTerminlationCallbackEmailAddresses',
    ];
    const externalControlNames = ['tenantPageExternalUrl'];

    for (const controlName of builtInControlNames) {
      const control = this.form.controls[controlName];
      if (this.tenantPageSource === TenantPageSource.BuiltIn) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    }

    for (const controlName of externalControlNames) {
      const control = this.form.controls[controlName];
      if (this.tenantPageSource === TenantPageSource.External) {
        control.enable({ emitEvent: false });
      } else {
        control.disable({ emitEvent: false });
      }
    }

    this.updateTenantPageExternalUrlValidation();
  }

  private updateTenantPageExternalUrlValidation(): void {
    const control = this.form.controls['tenantPageExternalUrl'] as FormControlEx;
    const isRequired = this.enabled && this.target === CompanySystemSettingsBulkTarget.TenantPages && this.tenantPageSource === TenantPageSource.External;

    control.required = isRequired;
    control.setValidators(isRequired ? [Validators.required, FormControlValidators.url()] : null);
    control.updateValueAndValidity({ emitEvent: false });
  }

  private splitList(value: string): string[] {
    return (value || '')
      .split(';')
      .map(item => item.trim())
      .filter(item => !!item);
  }

  private emailListValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const invalidAddress = value
      .split(';')
      .map(item => item.trim())
      .filter(item => !!item)
      .find(item => !emailRegex.test(item));

    return invalidAddress ? { email: true } : null;
  }
}
